import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputGroupComponent, NzInputModule } from 'ng-zorro-antd/input';
import { NzTableModule, NzTableQueryParams } from 'ng-zorro-antd/table';
import { DrawerService } from '../../../core/services/drawer.service';
import { BipTemplateService } from '../bip-template.service';
import {
  debounceTime,
  finalize,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { CommonService } from '../../../shared/services/common.service';
import { LoaderService } from '../../../core/services/loader.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { CommonModule } from '@angular/common';
import { AuditType } from '../bip-template.enums';
import { TemplateLogsFilterComponent } from '../template-logs/template-logs-filter/template-logs-filter.component';
import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import {
  DrawerParams,
  TemplateLogs,
  LogsQueryParams,
} from '../bip-template.interface';
import { TemplateLogDetailsComponent } from './template-log-details/template-log-details.component';
import { DrawerHeaderComponent } from '../../../components/drawer-header/drawer-header.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import {
  OrganizationDetailModel,
  userDetailModel,
} from '../../organizations/organization.interface';
import { UserDatePipe } from '../../../shared/pipes/user-date.pipe';
import { OrganizationSearchComponent } from '../../../components/organization-search/organization-search.component';
import { AuthService } from '../../../core/services/auth.service';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { TimeZoneService } from '../../../core/services/timezone.service';
import { format } from 'date-fns';

@Component({
  selector: 'app-template-logs',
  standalone: true,
  imports: [
    NzFormModule,
    NzInputGroupComponent,
    NzInputModule,
    NzIconModule,
    NzButtonModule,
    SvgIconComponent,
    NzTableModule,
    CommonModule,
    DrawerHeaderComponent,
    ReactiveFormsModule,
    NzSpinModule,
    UserDatePipe,
    OrganizationSearchComponent,
  ],
  templateUrl: './template-logs.component.html',
})
export class TemplateLogsComponent implements OnInit, OnDestroy {
  private readonly templateService = inject(BipTemplateService);

  @ViewChild('drawerLogDetail')
  drawerLogDetail!: TemplateRef<void>;
  pageSize = 10;
  pageIndex = 1;
  totalRow = 0;
  sort = 'title';
  searchTitle = new FormControl('');
  sortOrder: number | undefined = 1;
  templateLogs$ = this.templateService.templateLogs$;
  private readonly destroy$ = new Subject<void>();
  loading$: Observable<boolean> = of(false);
  appliedFilters: DrawerParams['appliedFilters'] | null = null;
  appliedFilterLength = 0;
  AuditTypeEnums = AuditType;
  orgId = 0;
  isExporting = false;
  drawerParams = {
    templateId: null,
    dataAuditTypeId: null,
    userId: null,
    dateFilter: null,
  };
  listOfColumn = [
    {
      title: 'Title',
      compare: null,
      key: 'title',
    },
    {
      title: 'Template',
      compare: null,
      key: 'templateName',
    },
    {
      title: 'Type',
      compare: null,
    },
    {
      title: 'User',
      compare: null,
      key: 'userName',
    },
    {
      title: 'Time',
      compare: null,
      key: 'time',
    },
    {
      title: 'Date',
      compare: null,
      key: 'date',
    },
    {
      title: '',
      compare: null,
    },
  ];
  orgData: OrganizationDetailModel | null = null;
  userData: userDetailModel | null = null;
  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly cdr: ChangeDetectorRef,
    private readonly authService: AuthService,
    private readonly orgSelectionService: OrganizationSelectionService,
    private readonly timeZoneService: TimeZoneService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.authService.userData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.userData = res;
    });
    this.drawerService
      .getPersistentState<DrawerParams>(DRAWER_IDS.TEMPLATE_LOGS_FILTER)
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        if (state) {
          this.appliedFilters = state.appliedFilters;
          this.appliedFilterLength = state.appliedFilterLength ?? 0;
          this.cdr.detectChanges();
        }
      });
  }

  ngOnInit(): void {
    this.orgSelectionService.orgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe((orgId = 0) => {
        const newOrgId = orgId;
        if (newOrgId !== this.orgId) {
          this.orgId = newOrgId ?? 0;
          this.pageIndex = 1;
          this.loadData();
        }
      });

    this.searchTitle.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(1000) // 500ms delay
      )
      .subscribe(() => {
        this.pageIndex = 1;
        this.loadData();
      });
  }
  onQueryParamsChange(params: NzTableQueryParams): void {
    this.pageSize = params.pageSize;
    this.pageIndex = params.pageIndex;
    const currentSort = params.sort.find(item => item.value !== null);
    this.sort = currentSort?.key || '';
    if (currentSort?.value) {
      this.sortOrder = currentSort.value === 'ascend' ? 0 : 1;
    } else {
      this.sortOrder = undefined;
    }
    this.loadData();
  }
  orgChange(value: number | null) {
    this.orgId = value ?? 0;
    this.orgSelectionService.setOrgId(value);
    this.loadData();
  }

  onChange() {
    this.pageIndex = 1;
    this.loadData();
  }

  loadData(): void {
    const queryParams = this.buildLogsQuery(this.pageIndex, this.pageSize);
    this.loaderService.show();
    this.templateService
      .getTemplateLogs(queryParams)
      .pipe(
        take(1), // Take only one response
        finalize(() => this.loaderService.hide()), // End loading after response
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          this.totalRow = response.result.totalRows;
          this.loaderService.hide();
        },
        error: (error: ErrorResponse) => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  onExportButtonClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.exportData();
  }

  exportData(): void {
    if (this.isExporting) {
      return;
    }

    if (!this.totalRow) {
      this.commonService.notify('info', 'No template logs available to export');
      return;
    }

    const exportQuery = this.buildLogsQuery(1, this.totalRow);
    this.isExporting = true;
    this.loaderService.show();

    this.templateService
      .getTemplateLogsForExport(exportQuery)
      .pipe(
        take(1),
        finalize(() => {
          this.isExporting = false;
          this.loaderService.hide();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          const items = response?.result?.items ?? [];
          if (!items.length) {
            this.commonService.notify(
              'info',
              'No template logs available to export'
            );
            return;
          }

          const csvRows = this.buildTemplateLogCsvRows(items);
          const fileName = `BIP_Template_Logs_${format(new Date(), 'yyyyMMdd_HHmmss')}`;
          this.downloadCsv(csvRows, fileName);
          this.commonService.notify(
            'success',
            'Template logs exported successfully'
          );
        },
        error: (error: ErrorResponse) => {
          this.commonService.notify(
            'error',
            error.message || 'Failed to export template logs'
          );
        },
      });
  }

  private buildLogsQuery(page: number, pageSize: number): LogsQueryParams {
    return {
      pageSize,
      page,
      sort: this.sort ?? undefined,
      sortOrder: this.sortOrder,
      title: this.searchTitle.value || '',
      organizationId: Number(this.orgId) || null,
      templateId: this.appliedFilters?.templateId || null,
      userId: this.appliedFilters?.userId || null,
      dataAuditTypeId: this.appliedFilters?.dataAuditTypeId || null,
      startDate: this.appliedFilters?.startDate || null,
      endDate: this.appliedFilters?.endDate || null,
    };
  }

  private buildTemplateLogCsvRows(templateLogs: TemplateLogs[]): string[][] {
    return [
      ['Title', 'Template', 'Type', 'User', 'Time', 'Date'],
      ...templateLogs.map(log => [
        log.title || '-',
        log.templateName || '-',
        this.getAuditTypeLabel(log.auditType),
        log.userName || '-',
        this.formatTime(log.time),
        this.formatDate(log.date),
      ]),
    ];
  }

  private downloadCsv(rows: string[][], fileName: string): void {
    const csvContent = rows
      .map(row => row.map(value => this.escapeCsvValue(value)).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = globalThis.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.csv`;
    link.click();
    link.remove();
    globalThis.URL.revokeObjectURL(url);
  }

  private escapeCsvValue(value: string): string {
    return `"${(value || '').replaceAll('"', '""')}"`;
  }

  private getAuditTypeLabel(auditType: string): string {
    if (auditType === this.AuditTypeEnums.CREATE) {
      return 'Added';
    }

    if (auditType === this.AuditTypeEnums.UPDATE) {
      return 'Updated';
    }

    if (auditType === this.AuditTypeEnums.DELETE) {
      return 'Deleted';
    }

    return auditType || '-';
  }

  private formatTime(value: string): string {
    if (!value) {
      return '-';
    }

    return this.formatInUserTimezone(value, 'hh:mm a');
  }

  private formatDate(value: string): string {
    if (!value) {
      return '-';
    }

    return this.formatInUserTimezone(value, 'MM/dd/yyyy');
  }

  private formatInUserTimezone(value: string, dateFormat: string): string {
    const formattedValue = this.timeZoneService.formatDate(
      value,
      dateFormat,
      this.timeZoneService.getUserTimeZone()
    );

    return formattedValue || '-';
  }

  openFilterDrawer(): void {
    this.drawerService
      .openDrawer(
        TemplateLogsFilterComponent,
        'Filter',
        780,
        true,
        undefined,
        {
          appliedFilterLength: this.appliedFilterLength,
          appliedFilters: this.appliedFilters,
          orgnizationId: this.orgId,
        },
        DRAWER_IDS.TEMPLATE_LOGS_FILTER
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (res) {
          if (this.appliedFilterLength !== 0) {
            this.pageIndex = 1;
            this.loadData();
          }
        }
      });
  }

  openTemplateLogDetailsDrawer(data: TemplateLogs): void {
    this.drawerService.openDrawer(
      TemplateLogDetailsComponent,
      this.drawerLogDetail as unknown as TemplateRef<object>,
      1170,
      true,
      undefined,
      {
        selectedLog: data,
      },
      DRAWER_IDS.TEMPLATE_LOGS_DETAIL
    );
  }

  closeLogDetailDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.TEMPLATE_LOGS_DETAIL);
  }

  clearFilters(): void {
    this.appliedFilters = null;
    this.appliedFilterLength = 0;
    this.pageIndex = 1;
    this.drawerService.clearPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER);
    this.loadData();
  }

  ngOnDestroy(): void {
    this.drawerService.clearPersistentState(DRAWER_IDS.TEMPLATE_LOGS_FILTER);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
